import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Layout } from 'antd';
import { useParams, useHistory, withRouter } from 'react-router-dom';
import { getAnnotationLabels } from '../../../lib/labels';
import useGlobal from '../../../store/Store';
import queryString from 'query-string';
import { getTestFile, setFileAnnotations } from '../../../lib/api/aws-api';
import { getTraining, startTraining, getTrainingInferenceList, deleteTraining } from '../../../lib/api/training-api';
import { getModel, deleteModel } from '../../../lib/api/algorithm-api';
import { getMultiflowStatus } from '../../../lib/api/flow-api';
import ResultSideBar from './ResultSideBar';
import ReviewMenuSideBar from './ReviewMenuSidebar';
import { PdfContainer } from '../../../common/pdf/PdfContainer';
import { InferenceFile, Inference, Model, PdfFile } from '../../../types';
import { Label, LabelSetting, TransformedLabel } from '../../../types/label';
import { ModalTitle } from '../../../components/text/Titles';
import { reformInferenceToPdfFile } from '../../../lib/util/reform';
import { MultiflowStatus } from '../../../types/flow';
import { TopBar } from '../../../components/layout/TopBar';
import { DetailContainer } from '../../../components/layout/DetailContainer';

const { Content } = Layout;

type Props = {
  location: { search: string };
};
const ReviewResult = (props: Props) => {
  let history = useHistory();

  const { trainingId } = useParams() as { trainingId: string };

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [model, setModel] = useState<null | Model>(null);
  const [inferenceFile, setInferenceFile] = useState<null | InferenceFile>(null);
  const [pdfFile, setPdfFile] = useState<PdfFile>();
  const [inferenceList, setInferenceList] = useState<null | Inference[]>(null);
  const [isCreateModel, setIsCreateModel] = useState<boolean>(false);
  const [globalState, globalActions] = useGlobal();
  const [multiflowStatus, setMultiflowStatus] = useState<null | MultiflowStatus>(null);

  const onInferenceSelect = useCallback(
    async inferenceId => {
      const inferenceFile = await getTestFile(inferenceId);
      console.log(inferenceFile);

      if (inferenceFile.model.algorithm.multiflow && inferenceFile.run && inferenceFile.run.id) {
          try {
            const multiflowStatus: MultiflowStatus = await getMultiflowStatus(inferenceFile.run.id);
            console.log('multi:', multiflowStatus);
            setMultiflowStatus(multiflowStatus);
          } catch (e) {
            console.log(e);
          }
      }

      setInferenceFile(inferenceFile);
      console.log(reformInferenceToPdfFile(inferenceFile));
      setPdfFile(reformInferenceToPdfFile(inferenceFile));

      if (inferenceFile && inferenceFile.model) {
        const labels = getAnnotationLabels(inferenceFile, inferenceFile.model);
        globalActions.setLabels(labels);
      }
    },
    [setInferenceFile, globalActions]
  );

  const reset = useCallback(() => {
    globalActions.setLabelOptions(null);
    globalActions.setLabelEdit(null);
    globalActions.removeLabels();
    setInferenceFile(null);
  }, [globalActions, setInferenceFile]);

  useEffect(() => {
    const fetchData = async () => {
      const training = await getTraining(trainingId);
      const model = await getModel(training.modelId);
      setModel(model);
      const annotationTypes = model && model.info && model.info.annotationTypes;
      const annotationLabels: LabelSetting[] = annotationTypes.map(type => ({
        name: type,
        color: '',
        hidden: false,
      }));
      globalActions.setLabelOptions(annotationLabels);

      const inferenceList = await getTrainingInferenceList(trainingId);
      setInferenceList(inferenceList);
      if (inferenceList && inferenceList.length > 0) {
        const inferenceId = inferenceList[0].id;
        onInferenceSelect(inferenceId);
      }
    };

    fetchData();

    let url = props.location.search;
    let params = queryString.parse(url);
    if (params.readOnly && params.readOnly === 'true') {
      globalActions.setReadOnly(true);
    }

    if (params.createModel && params.createModel === 'true') {
      setIsCreateModel(true);
    }

    return () => {
      reset();
      globalActions.setReadOnly(false);
    };
  }, [globalActions, onInferenceSelect, props.location.search, reset, trainingId]);

  const removeTraining = async () => {
    await deleteTraining(trainingId);

    if (isCreateModel && model) {
      await deleteModel(model.id);
    }

    goBack();
  };

  const onTrain = async () => {
    if (!model) return;
    await startTraining(trainingId);
    history.push(`/custom/${model.algorithm.id}/model/${model.id}`);
  };

  const goBack = () => {
    history.push(`/custom/${model?.algorithm.id}/model/${model?.id}`);
  };

  const onAnnotationChange = async (newLabels: Label[]) => {
    if (inferenceFile) {
      const oldLabels = globalState.labels;
      const strippedLabels: TransformedLabel[] = newLabels.map(({ source, page, location, word, ...label }) => {
        const positions =
          page && location
            ? [
                {
                  source,
                  page,
                  location,
                  word,
                },
              ]
            : [];
        return {
          ...label,
          positions,
        };
      });

      try {
        globalActions.setLabels(newLabels);
        await setFileAnnotations(inferenceFile.id, { fields: strippedLabels });
      } catch (e) {
        globalActions.setLabels(oldLabels);
      }
    }
  };

  const onClassChange = async (className: string) => {
    if (!inferenceFile) return;
    const payload = {
      type: {
        reliable: true,
        name: className,
      },
      fields: [],
    };

    await setFileAnnotations(inferenceFile.id, payload);
    await onInferenceSelect(inferenceFile.id);
  };

  const { labels } = globalState;
  const filteredLabels = useMemo(() => labels.filter(label => label.exists || !label.reliable), [labels]);

  return (
    <>
      <Modal
        visible={modalVisible}
        title={
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <ModalTitle>Are you sure?</ModalTitle>
          </div>
        }
        footer={
          <div style={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
            <Button type="default" style={{ width: '50%' }} onClick={() => setModalVisible(false)}>
              Cancel
            </Button>
            <Button /* type="danger" */ style={{ width: '50%' }} onClick={removeTraining}>
              Yes, remove
            </Button>
          </div>
        }
        onCancel={() => setModalVisible(false)}>
        <p>The model will not be trained and your documents will be removed. Are you sure you want to continue?</p>
      </Modal>
      <TopBar
        title={globalState.readOnly ? 'Reviewed' : 'Review & train'}
        closeFunction={(event: any) => {
          event.preventDefault();
          event.stopPropagation();
          if (globalState.readOnly) {
            goBack();
          } else {
            setModalVisible(true);
          }
        }}
      />
      <DetailContainer>
        <ReviewMenuSideBar
          inferenceList={inferenceList}
          selectedId={inferenceFile && inferenceFile.id}
          onInferenceClick={onInferenceSelect}
          onTrain={onTrain}
          readOnly={globalState.readOnly}
        />
        <Layout>
          <Content>
            <div className={'pdf-layout'} style={{ width: '100%' }}>
              <PdfContainer
                file={pdfFile}
                labels={filteredLabels}
                showNonExisting={false}
                newLabelAllowed={true}
                onAnnotationChange={onAnnotationChange}
              />
            </div>
          </Content>
        </Layout>
        {inferenceFile && (
          <ResultSideBar
            multiflowStatus={multiflowStatus}
            inferenceFile={inferenceFile}
            onAnnotationChange={onAnnotationChange}
            onClassChange={onClassChange}
          />
        )}
      </DetailContainer>
    </>
  );
};

export default withRouter(ReviewResult);
